import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { IconMinus, IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { resolvePatchLogCopy } from './i18n';
import type { PatchLogLocale } from './types';

/** Above the patch-log modal (9000), below Mantine notifications (10001). */
export const IMAGE_ZOOM_Z_INDEX = 9500;

const MIN_SCALE = 1;
const MAX_SCALE = 6;
/** One wheel notch (~100px of deltaY) is about a 12% step. */
const WHEEL_SENSITIVITY = 0.0012;
/** Keyboard +/- and the on-screen buttons move in fixed, predictable steps. */
const BUTTON_STEP = 1.4;
/** Arrow-key pan, in screen pixels. */
const KEY_PAN_PX = 60;

/** Tab order inside the dialog. The backdrop is deliberately excluded (tabindex="-1"). */
const FOCUSABLE = 'button:not([disabled]):not([tabindex="-1"]), [href], [tabindex]:not([tabindex="-1"])';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const IDENTITY: Transform = { scale: 1, x: 0, y: 0 };

export interface ImageZoomViewerProps {
  src: string;
  /** Already resolved for the active locale by the caller; announced as the dialog's name. */
  alt: string;
  /** Intrinsic size from the API, when known — keeps the frame from reflowing as it loads. */
  width?: number;
  height?: number;
  locale: PatchLogLocale;
  onClose: () => void;
}

/**
 * Hand-rolled full-screen image viewer (Issue 7). No lightbox dependency: the
 * whole surface is one transformed <img>, and every gesture writes the same
 * {scale, x, y} triple.
 *
 * Input, all on the same state:
 *  - wheel / trackpad scroll zooms around the pointer (native listener, because
 *    React's onWheel is passive and cannot preventDefault the page scroll);
 *  - two pointers pinch around their midpoint (`touch-action: none` on the
 *    surface, so the browser never steals the gesture);
 *  - one pointer drags to pan once zoomed in;
 *  - +/-/0 and the arrow keys do the same from the keyboard, and the three
 *    48px controls are the pointer-free equivalent.
 *
 * Accessibility: role="dialog" + aria-modal, named by the image's alt text in
 * the active locale, focus trapped inside while open, Esc closes, and focus
 * returns to whatever opened it.
 */
export const ImageZoomViewer = ({ src, alt, width, height, locale, onClose }: ImageZoomViewerProps) => {
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  /** Live pointers on the surface, by id — two of them means a pinch. */
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  /** Distance and midpoint of the pinch when the second finger landed. */
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);

  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const transformRef = useRef<Transform>(IDENTITY);
  transformRef.current = transform;

  const t = useCallback((key: Parameters<typeof resolvePatchLogCopy>[1]) => resolvePatchLogCopy(locale, key), [locale]);

  // A new image always opens un-zoomed and centered.
  useEffect(() => {
    setTransform(IDENTITY);
    pointersRef.current.clear();
    pinchRef.current = null;
  }, [src]);

  /**
   * Keeps the image from being dragged off screen: at scale s the picture
   * overflows its box by (s - 1) * size, so the pan may use half of that in
   * either direction.
   */
  const clampPan = useCallback((next: Transform): Transform => {
    const box = surfaceRef.current?.getBoundingClientRect();
    if (!box) {
      return next;
    }
    const maxX = Math.max(0, (box.width * (next.scale - 1)) / 2);
    const maxY = Math.max(0, (box.height * (next.scale - 1)) / 2);
    return { scale: next.scale, x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
  }, []);

  /**
   * Zooms to `nextScale` while holding the point under (clientX, clientY)
   * still. With the transform written as `translate(x, y) scale(s)` about the
   * center, a point p maps to p*s + t, so keeping it fixed means
   * t' = c - (c - t) * s'/s.
   */
  const zoomAround = useCallback(
    (nextScaleRaw: number, clientX?: number, clientY?: number) => {
      const box = surfaceRef.current?.getBoundingClientRect();
      const current = transformRef.current;
      const nextScale = clamp(nextScaleRaw, MIN_SCALE, MAX_SCALE);
      if (nextScale === current.scale) {
        return;
      }
      if (!box || clientX === undefined || clientY === undefined) {
        setTransform(clampPan({ scale: nextScale, x: current.x, y: current.y }));
        return;
      }
      const cx = clientX - (box.left + box.width / 2);
      const cy = clientY - (box.top + box.height / 2);
      const ratio = nextScale / current.scale;
      setTransform(
        clampPan({
          scale: nextScale,
          x: cx - (cx - current.x) * ratio,
          y: cy - (cy - current.y) * ratio,
        })
      );
    },
    [clampPan]
  );

  const panBy = useCallback(
    (dx: number, dy: number) => {
      const current = transformRef.current;
      setTransform(clampPan({ scale: current.scale, x: current.x + dx, y: current.y + dy }));
    },
    [clampPan]
  );

  const reset = useCallback(() => setTransform(IDENTITY), []);

  // Wheel/trackpad zoom. Registered natively with `passive: false` because
  // React routes onWheel through a passive root listener, so preventDefault()
  // there is ignored and the page behind would scroll.
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * WHEEL_SENSITIVITY);
      zoomAround(transformRef.current.scale * factor, event.clientX, event.clientY);
    };
    surface.addEventListener('wheel', onWheel, { passive: false });
    return () => surface.removeEventListener('wheel', onWheel);
  }, [zoomAround]);

  // Esc closes; the zoom keys mirror the on-screen controls; Tab is trapped.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key === 'Tab') {
        const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (!nodes || nodes.length === 0) {
          return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (event.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomAround(transformRef.current.scale * BUTTON_STEP);
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomAround(transformRef.current.scale / BUTTON_STEP);
      } else if (event.key === '0') {
        event.preventDefault();
        reset();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        panBy(KEY_PAN_PX, 0);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        panBy(-KEY_PAN_PX, 0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        panBy(0, KEY_PAN_PX);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        panBy(0, -KEY_PAN_PX);
      }
    };
    // Capture phase: the viewer is rendered in a portal, so an Esc must not
    // reach a Mantine modal underneath and close that instead.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, zoomAround, panBy, reset]);

  // Move focus in on open and hand it back to the trigger on close; the page
  // behind must not scroll while the viewer owns the screen.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: transformRef.current.scale };
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pointers = pointersRef.current;
    const previous = pointers.get(event.pointerId);
    if (!previous) {
      return;
    }
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const pinch = pinchRef.current;
      if (pinch && pinch.distance > 0) {
        // Zoom about the midpoint of the two fingers, so the pinch stays put.
        zoomAround((pinch.scale * distance) / pinch.distance, (a.x + b.x) / 2, (a.y + b.y) / 2);
      }
      return;
    }

    // Single pointer: drag to pan, but only when there is something to pan.
    if (transformRef.current.scale > MIN_SCALE) {
      panBy(event.clientX - previous.x, event.clientY - previous.y);
    }
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) {
      pinchRef.current = null;
    }
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const zoomed = transform.scale > MIN_SCALE;
  const percent = Math.round(transform.scale * 100);
  const label = alt.trim() || t('imageViewer');

  const imageStyle = useMemo(
    () => ({
      transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
      cursor: zoomed ? 'grab' : 'zoom-in',
    }),
    [transform, zoomed]
  );

  const body = (
    <div
      ref={dialogRef}
      className="jarvis-patch-log-zoom"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      style={{ zIndex: IMAGE_ZOOM_Z_INDEX }}
    >
      {/* The backdrop is a button so a click outside the picture closes the
          viewer without inventing a click handler on a non-interactive div. */}
      <button
        type="button"
        tabIndex={-1}
        className="jarvis-patch-log-zoom__backdrop"
        aria-label={t('close')}
        onClick={onClose}
      />

      <div
        ref={surfaceRef}
        className="jarvis-patch-log-zoom__surface"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={(event) => {
          if (zoomed) {
            reset();
          } else {
            zoomAround(2, event.clientX, event.clientY);
          }
        }}
      >
        <img
          className="jarvis-patch-log-zoom__image"
          src={src}
          alt={alt}
          width={width && width > 0 ? width : undefined}
          height={height && height > 0 ? height : undefined}
          style={imageStyle}
          draggable={false}
          decoding="async"
        />
      </div>

      <button
        ref={closeRef}
        type="button"
        className="jarvis-patch-log-zoom__close"
        aria-label={t('close')}
        onClick={onClose}
      >
        <IconX size={26} stroke={2} aria-hidden="true" />
      </button>

      <div className="jarvis-patch-log-zoom__controls">
        <button
          type="button"
          className="jarvis-patch-log-zoom__control"
          aria-label={t('zoomOut')}
          onClick={() => zoomAround(transform.scale / BUTTON_STEP)}
          disabled={transform.scale <= MIN_SCALE}
        >
          <IconMinus size={22} stroke={2} aria-hidden="true" />
        </button>
        <span className="jarvis-patch-log-zoom__percent" aria-live="polite">{`${percent}%`}</span>
        <button
          type="button"
          className="jarvis-patch-log-zoom__control"
          aria-label={t('zoomIn')}
          onClick={() => zoomAround(transform.scale * BUTTON_STEP)}
          disabled={transform.scale >= MAX_SCALE}
        >
          <IconPlus size={22} stroke={2} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="jarvis-patch-log-zoom__control"
          aria-label={t('resetZoom')}
          onClick={reset}
          disabled={!zoomed}
        >
          <IconRefresh size={20} stroke={2} aria-hidden="true" />
        </button>
      </div>

      <p className="jarvis-patch-log-zoom__hint">{t('zoomHint')}</p>
    </div>
  );

  return typeof document === 'undefined' ? null : createPortal(body, document.body);
};

export default ImageZoomViewer;
